import * as React from 'react';
import { FieldProps } from 'formik';
import { Field, Input } from 'formik-antd';
import { Button } from 'antd';
import NumberFormat, {
  NumberFormatProps,
  NumberFormatValues,
} from 'react-number-format';
import { useRef } from 'react';
import { FormikFieldProps } from 'formik-antd/lib/FieldProps';
import cx from 'classnames';
import { isUndefined } from 'lodash';

import styles from './InputNumber.module.less';

type InputNumberProps = FormikFieldProps & NumberFormatProps & CustomInputProps;

interface CustomInputProps {
  hideSpinners?: boolean;
  precision?: number;
  inputPrefix?: string;
  addonAfter?: React.ReactNode;
  canBeNegative?: boolean;
}

function CustomInput(props: any) {
  const {
    onKeyDown,
    precision,
    canBeNegative,
    step,
    disabled,
    hideSpinners,
    inputPrefix,
    ...restProps
  } = props;
  const inputRef: any = useRef();

  const updateValue = (value: number) => {
    inputRef.current.input.value = value;

    const event = new Event('change');
    inputRef.current.input.dispatchEvent(event);
    props.onChange(event);
  };

  const handleUpClick = () => {
    const floatValue = parseFloat(props.value) || 0;
    const fixedValue = parseFloat((floatValue + step).toFixed(precision));

    updateValue(fixedValue);
  };

  const handleDownClick = () => {
    const floatValue = parseFloat(props.value) || 0;
    let fixedValue = parseFloat((floatValue - step).toFixed(precision));

    if (!canBeNegative && fixedValue < 0) {
      fixedValue = 0;
    }

    updateValue(fixedValue);
  };

  return (
    <Input
      {...restProps}
      disabled={disabled}
      ref={inputRef}
      prefix={inputPrefix}
      suffix={
        !hideSpinners ? (
          <div className={cx('input-number-button', styles.buttonsWrapper)}>
            <Button
              icon={<span className="icon icon-up" />}
              onClick={handleUpClick}
              className={styles.spinnerButton}
              disabled={disabled}
            />

            <Button
              icon={<span className="icon icon-down" />}
              onClick={handleDownClick}
              className={styles.spinnerButton}
              disabled={disabled}
            />
          </div>
        ) : (
          // https://ant.design/components/input#why-input-lose-focus-when-change-prefixsuffixshowcount
          <span />
        )
      }
      autoComplete="off"
    />
  );
}

const isAllowed = ({ floatValue }: NumberFormatValues) => {
  return (
    !floatValue || (floatValue >= -999999999999 && floatValue <= 999999999999)
  );
};

export default function InputNumber({
  name,
  validate,
  fast,
  onBlur: $onBlur,
  onValueChange: $onValueChange,
  allowNegative = false,
  fixedDecimalScale = false,
  precision = 0,
  step = 1.0,
  hideSpinners = false,
  disabled,
  ...restProps
}: InputNumberProps) {
  return (
    <Field name={name} validate={validate} fast={fast}>
      {({
        field: { onBlur, name, value },
        form: { setFieldValue },
      }: FieldProps) => (
        <NumberFormat<CustomInputProps>
          name={name}
          value={value}
          decimalScale={precision}
          allowNegative={allowNegative}
          fixedDecimalScale={fixedDecimalScale}
          isAllowed={isAllowed}
          onValueChange={(values, sourceInfo) => {
            if (disabled) return;

            setFieldValue(
              name,
              !isUndefined(values.floatValue) ? values.floatValue : null,
            );

            $onValueChange?.(values, sourceInfo);
          }}
          onBlur={(event: any) => {
            onBlur(event);
            $onBlur?.(event);
          }}
          customInput={CustomInput as any}
          // additional props for the custom input below
          precision={precision}
          step={step}
          canBeNegative={allowNegative}
          hideSpinners={hideSpinners}
          disabled={disabled}
          // // rest props
          {...restProps}
        />
      )}
    </Field>
  );
}
